import React, { useEffect, useState } from "react";

interface ICheckBoxProps {
  title?: string;
  onChange: (value: boolean) => void;
  checked?: boolean; // Controlled prop for external state management
  disabled?: boolean; // Optional prop to disable the checkbox
}

const CheckBox: React.FC<ICheckBoxProps> = ({
  title,
  onChange,
  checked: controlledChecked,
  disabled = false,
}) => {
  // Internal state for uncontrolled usage
  const [internalChecked, setInternalChecked] = useState<boolean>(false);

  // Determine whether the component is controlled or uncontrolled
  const isControlled = controlledChecked !== undefined;

  // Handle toggle behavior
  const handleToggle = () => {
    if (disabled) return; // Do nothing if disabled

    // If controlled, notify parent; otherwise, update internal state
    if (isControlled) {
      onChange(!controlledChecked);
    } else {
      const newValue = !internalChecked;
      setInternalChecked(newValue);
      onChange(newValue); // Notify the parent about the change
    }
  };

  // Sync internal state with controlled prop
  useEffect(() => {
    if (isControlled) {
      setInternalChecked(controlledChecked!);
    }
  }, [controlledChecked, isControlled]);

  return (
    <div
      className={`rounded-md flex flex-col items-center justify-center gap-2 select-none ${
        disabled ? "cursor-not-allowed text-gray-400" : "cursor-pointer"
      }`}
      onClick={handleToggle} // Toggle on click if not disabled
    >
      <div
        className={`h-6 w-6 flex rounded-md items-center justify-center ${
          disabled ? "bg-sky-600" : "bg-sky-600"
        } `}
      >
        {internalChecked ? (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            className={`w-6 h-6 font-semibold ${
              disabled ? "text-sky-600" : "text-sky-600"
            }`}
          >
            <rect x="2" y="2" width="20" height="20" rx="3" />
            <path
              fillRule="evenodd"
              d="M7 12.5l4 4L18 9.5"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ) : (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            className={`w-8 h-8 font-semibold ${
              disabled ? "text-gray-400" : "text-white"
            }`}
          >
            <rect x="2" y="2" width="20" height="20" rx="3" />
          </svg>
        )}
      </div>
      {title && (
        <span
          className={`font-semibold text-md ${disabled ? "text-gray-400" : ""}`}
        >
          {title}
        </span>
      )}
    </div>
  );
};

export default CheckBox;
